/* assets/race-search-core.js — shared core for the Race Finder section
 * (assets/race-finder.js) and the standalone Race Search section
 * (assets/race-search.js). Created 13 July 2026.
 *
 * Contains everything both sections need to behave identically:
 *   - text/title helpers (normalizeText, raceBaseTitle, query aliases, …)
 *   - IndexLoader: fetches the compact race index through the Section
 *     Rendering API (sections/race-finder-data.liquid) and shares ONE
 *     sessionStorage cache across sections — the key is derived from the
 *     data version + the sorted collection handles, so both sections must
 *     be configured with the same collections to share it.
 *   - Autocomplete: the suggestion dropdown mechanics (debounce, keyboard
 *     nav, focus-reopen suppression, pointerdown delegation).
 *   - createLogoAlign: pads section edges to line up with the header logo.
 *
 * This file must be loaded BEFORE race-finder.js / race-search.js (both
 * sections emit the script tags in that order; `defer` preserves it).
 */
(function () {
  'use strict';

  // Both sections emit a <script> tag for this file; the second execution
  // (same page or theme-editor re-render) must be a no-op.
  if (window.MMCRaceSearch) return;

  const DATA_SECTION_ID = 'race-finder-data';
  const FETCH_CONCURRENCY = 6;
  const RETRY_DELAYS_MS = [1000, 4000];

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function hashString(str) {
    let h = 5381;
    for (let i = 0; i < str.length; i++) {
      h = ((h << 5) + h + str.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(36);
  }

  function normalizeText(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  /* Dark/Light editions of the same race are standalone products with
     identical coordinates. The base title bundles them: titles follow
     "Amica Newport Marathon – A4 Dark Edition – 3D Race Frame". */
  function raceBaseTitle(title) {
    const kept = (title || '')
      .split('–')
      .filter((segment) => !/edition|race\s*frame/i.test(segment));
    const base = kept.join('–').replace(/\s+/g, ' ').trim();
    return base || (title || '').trim();
  }

  function raceGroupKey(record) {
    return (
      normalizeText(raceBaseTitle(record.t)) +
      '|' + record.y.toFixed(4) + ',' + record.x.toFixed(4)
    );
  }

  function variantLabel(title, index, labels) {
    if (/dark\s*edition/i.test(title || '')) return (labels && labels.dark) || 'Dark';
    if (/light\s*edition/i.test(title || '')) return (labels && labels.light) || 'Light';
    const pattern = (labels && labels.edition) || 'Edition {num}';
    return pattern.replace('{num}', String(index + 1));
  }

  /* Sort order is derived from the TITLE, never from the (localizable)
     display label — Dark first, Light second, everything else after. */
  function variantOrder(record) {
    const title = (record && record.t) || '';
    if (/dark\s*edition/i.test(title)) return 0;
    if (/light\s*edition/i.test(title)) return 1;
    return 2;
  }

  // Display labels for racetype TAG keys (tag values stay internal).
  // Trademark-safe: no "Ironman"/"IM" anywhere customer-facing.
  const TYPE_LABELS = { ironman: 'Triathlon' };
  // Query rewrites applied before matching, e.g. "iron man" -> "ironman"
  // so trademark searches still surface the triathlons via the racetype tag.
  const QUERY_ALIASES = [[/iron\s+man/g, 'ironman']];
  function normalizeQuery(q) {
    let normalized = normalizeText(q);
    QUERY_ALIASES.forEach((pair) => {
      normalized = normalized.replace(pair[0], pair[1]);
    });
    return normalized;
  }

  /* Free-text matching shared by both sections: the haystack includes the
     distance/racetype keys and the distance label, so searches like
     "triathlon", "70.3" or "ironman" (via the internal tag) find the right
     races even without the word in the title. */
  function matchesQuery(record, q, distanceLabel) {
    const haystack = normalizeText(
      record.t +
        ' ' +
        (record.c || '') +
        ' ' +
        (record.d ? record.d + ' ' + distanceLabel(record.d) : '') +
        ' ' +
        (record.ty ? record.ty + ' ' + (TYPE_LABELS[record.ty] || '') : '')
    );
    return haystack.indexOf(normalizeQuery(q)) !== -1;
  }

  function fmt(template, values) {
    return (template || '').replace(/\{(\w+)\}/g, (m, key) =>
      key in values ? values[key] : m
    );
  }

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Accepts "51.16" and German "51,16"; returns null for anything that is not
  // a plausible coordinate (so callers can fall back instead of flying to 0/0).
  function parseCoord(value, absMax) {
    if (value === null || value === undefined) return null;
    const n = parseFloat(String(value).trim().replace(',', '.'));
    return Number.isFinite(n) && Math.abs(n) <= absMax ? n : null;
  }

  /* Order for the collections config: ascending numeric `sort`, entries
     without one keep their relative position at the end. Metaobject entries
     have no guaranteed order in Liquid, so the `sort` field is the only
     reliable way to control e.g. the race type dropdown order. */
  function sortCollections(list) {
    return list
      .map((collection, index) => [collection, index])
      .sort((a, b) => {
        const sa = typeof a[0].sort === 'number' ? a[0].sort : Infinity;
        const sb = typeof b[0].sort === 'number' ? b[0].sort : Infinity;
        return sa - sb || a[1] - b[1];
      })
      .map((pair) => pair[0]);
  }

  /* ---------- index loader ----------
     Owns the record map and the whole fetch/merge/cache pipeline. Consumers
     get notified through callbacks:
       onUpdate()    — a page of records was ingested (schedule a re-render)
       onNewRecord() — at least one new record appeared (sorted views dirty)
     start() resolves once loading is complete (instantly on cache hit). */
  class IndexLoader {
    constructor(opts) {
      this.collections = opts.collections || [];
      this.collectionByHandle = new Map(this.collections.map((c) => [c.handle, c]));
      this.dataVersion = opts.dataVersion;
      this.dataSectionId = opts.dataSectionId || DATA_SECTION_ID;
      this.onUpdate = opts.onUpdate || function () {};
      this.onNewRecord = opts.onNewRecord || function () {};

      this.records = new Map(); // handle -> record ({...index fields, cols: Set})
      this.failedCollections = new Set();
      this.collectionStats = new Map(); // handle -> {expected, done, items}
      this.loadComplete = false;
      this.startPromise = null;
    }

    get loadStarted() {
      return this.startPromise !== null;
    }

    get cacheKey() {
      const handles = this.collections.map((c) => c.handle).sort().join(',');
      return 'rf:index:v' + this.dataVersion + ':' + hashString(handles);
    }

    start() {
      if (this.startPromise) return this.startPromise;

      const cached = sessionGet(this.cacheKey);
      if (cached) {
        try {
          const items = JSON.parse(cached).items;
          items.forEach((item) => {
            const record = Object.assign({}, item, { cols: new Set(item.cols) });
            this.records.set(record.h, record);
          });
          this.onNewRecord();
          this.loadComplete = true;
          this.startPromise = Promise.resolve({ fromCache: true });
          return this.startPromise;
        } catch (e) {
          this.records.clear();
        }
      }

      this.startPromise = this.fetchAll(this.collections.map((c) => c.handle)).then(() => {
        this.loadComplete = true;
        if (this.failedCollections.size === 0) this.saveCache();
        return { fromCache: false };
      });
      return this.startPromise;
    }

    retryFailed() {
      const handles = Array.from(this.failedCollections);
      this.failedCollections.clear();
      this.loadComplete = false;
      return this.fetchAll(handles).then(() => {
        this.loadComplete = true;
        if (this.failedCollections.size === 0) this.saveCache();
      });
    }

    async fetchAll(handles) {
      const queue = handles.map((handle) => ({ handle, page: 1 }));
      handles.forEach((handle) => {
        this.collectionStats.set(handle, { expected: null, done: 0, items: 0 });
      });
      // Workers must not exit while any fetch is still in flight: page-1
      // responses push the follow-up page jobs for large collections, and an
      // idle worker has to stay alive to pick those up in parallel.
      let inFlight = 0;
      const worker = async () => {
        for (;;) {
          const job = queue.shift();
          if (!job) {
            if (inFlight === 0) return;
            await delay(50);
            continue;
          }
          if (this.failedCollections.has(job.handle)) continue;
          inFlight += 1;
          try {
            const payload = await this.fetchPage(job.handle, job.page);
            this.ingest(job, payload, queue);
          } catch (e) {
            this.failedCollections.add(job.handle);
          } finally {
            inFlight -= 1;
          }
        }
      };
      const workers = [];
      for (let i = 0; i < FETCH_CONCURRENCY; i++) {
        workers.push(worker());
      }
      await Promise.all(workers);
    }

    async fetchPage(handle, page) {
      const routesRoot =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url =
        routesRoot + 'collections/' + encodeURIComponent(handle) +
        '?section_id=' + this.dataSectionId + '&page=' + page;
      for (let attempt = 0; ; attempt++) {
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error('HTTP ' + response.status);
          const text = await response.text();
          // The Section Rendering API wraps the JSON in a section <div>.
          const start = text.indexOf('{');
          const end = text.lastIndexOf('}');
          if (start === -1 || end <= start) throw new Error('Unexpected payload');
          return JSON.parse(text.slice(start, end + 1));
        } catch (e) {
          if (attempt >= RETRY_DELAYS_MS.length) throw e;
          await delay(RETRY_DELAYS_MS[attempt]);
        }
      }
    }

    ingest(job, payload, queue) {
      const stats = this.collectionStats.get(job.handle);
      if (!payload.col || !Array.isArray(payload.items)) {
        stats.expected = 0;
        return;
      }
      if (job.page === 1) {
        stats.expected = payload.pp || 1;
        for (let p = 2; p <= stats.expected; p++) {
          queue.push({ handle: job.handle, page: p });
        }
      }
      payload.items.forEach((item) => this.merge(item, job.handle));
      stats.done += 1;
      stats.items += payload.items.length;
      if (stats.done >= stats.expected) this.applyFallbacks(job.handle);
      this.onUpdate();
    }

    merge(item, collectionHandle) {
      // Coordinates must be plausible numbers; otherwise the record is grid-only.
      if (
        typeof item.y !== 'number' || typeof item.x !== 'number' ||
        Math.abs(item.y) > 90 || Math.abs(item.x) > 180
      ) {
        delete item.y;
        delete item.x;
      }
      let record = this.records.get(item.h);
      if (!record) {
        record = Object.assign({}, item, { cols: new Set() });
        this.records.set(item.h, record);
        this.onNewRecord();
      } else {
        // Same product seen via another collection: fill fields still missing.
        Object.keys(item).forEach((key) => {
          if (record[key] === undefined) record[key] = item[key];
        });
      }
      record.cols.add(collectionHandle);
    }

    // Collection-block fallbacks (country_code/region) fill blanks only —
    // tag/metafield data emitted by the data section always outranks them.
    applyFallbacks(handle) {
      const config = this.collectionByHandle.get(handle);
      if (!config || (!config.cc && !config.region)) return;
      this.records.forEach((record) => {
        if (!record.cols.has(handle)) return;
        if (!record.cc && config.cc) record.cc = config.cc;
        if (!record.r && config.region) record.r = config.region;
      });
    }

    saveCache() {
      const items = [];
      this.records.forEach((record) => {
        items.push(Object.assign({}, record, { cols: Array.from(record.cols) }));
      });
      sessionSet(this.cacheKey, JSON.stringify({ items }));
    }
  }

  /* ---------- autocomplete ----------
     Owns the mechanics of the suggestion dropdown; the CONTENT comes from the
     consumer via getItems(raw) -> { items, total, allPick } where each item is
     { img, title, sub, pick } (pick is opaque and handed back to onPick).

     Hard-won behavior, do not regress:
     - Picks act on `pointerdown` via DELEGATION on the container (per-option
       listeners caused a rebuild-race that swallowed real clicks). The click
       branch serves the keyboard path (Enter → element.click()).
     - suppress() blocks the focus-triggered reopen right after a pick (mobile
       keyboards synthesize a focus event on tap).
     - The focus handler only (re)opens when the list is CLOSED — rebuilding an
       OPEN list would detach a pressed option mid-click and swallow the click. */
  class Autocomplete {
    constructor(opts) {
      this.input = opts.input;
      this.container = opts.container || null;
      this.scope = opts.scope || this.input.parentElement;
      this.enabled = opts.enabled !== false && Boolean(this.container);
      this.idPrefix = opts.idPrefix || 'rf-opt-';
      this.minChars = opts.minChars || 2;
      this.debounceMs = opts.debounceMs === undefined ? 150 : opts.debounceMs;
      this.strings = opts.strings || {}; // { empty, allOne, allMany }
      this.formatNumber = opts.formatNumber || String;
      this.getItems = opts.getItems;
      this.onPick = opts.onPick;
      this.onClear = opts.onClear || function () {};
      this.onType = opts.onType || function () {};
      this.canOpen = opts.canOpen || function () { return true; };

      this.entries = []; // rendered option elements
      this.picks = []; // pick payloads, parallel to the item entries
      this.allPick = null;
      this.active = -1;
      this.timer = null;
      this.suppressUntil = 0; // no reopen right after a pick

      this.bind();
    }

    bind() {
      this.input.addEventListener('input', () => {
        const raw = this.input.value.trim();
        window.clearTimeout(this.timer);
        if (raw === '') {
          // Clearing the field live-resets the search in the consumer.
          this.close();
          this.onClear();
          return;
        }
        if (!this.enabled) return;
        if (raw.length < this.minChars) {
          this.close();
          return;
        }
        this.onType();
        this.timer = window.setTimeout(() => this.render(raw), this.debounceMs);
      });

      this.input.addEventListener('keydown', (event) => {
        if (!this.container || this.container.hidden) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.move(1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.move(-1);
        } else if (event.key === 'Enter' && this.active >= 0) {
          event.preventDefault();
          this.entries[this.active].click();
        } else if (event.key === 'Escape') {
          event.stopPropagation(); // close only the suggestions, not the sheets
          this.close();
        }
      });

      this.input.addEventListener('focus', () => {
        const raw = this.input.value.trim();
        // Only (re)open when the list is closed — rebuilding an OPEN list
        // would detach a pressed option mid-click and swallow the click.
        if (
          this.enabled &&
          raw.length >= this.minChars &&
          this.canOpen() &&
          this.container.hidden &&
          Date.now() > this.suppressUntil
        ) {
          this.render(raw);
        }
      });

      // Option picking via DELEGATION on the container (which is never
      // replaced): even if the list re-renders mid-interaction, the pick
      // still lands. TAP DETECTION: pointerdown only ARMS the pick (its
      // preventDefault keeps the input focused — an intermediate blur would
      // re-render the list and swallow the pick, the original race);
      // the pick fires on pointerup, and any real movement in between
      // (finger scrolling the list) or a pointercancel disarms it. The
      // click branch serves the keyboard path (Enter → element.click())
      // and skips the synthetic click that follows a pointerup pick.
      if (this.container) {
        const doPick = (option) => {
          const key = option.getAttribute('data-rf-pick');
          this.onPick(key === 'all' ? this.allPick : this.picks[parseInt(key, 10)]);
        };
        const SLOP = 12; // px of movement that still counts as a tap
        let armed = null;
        let startX = 0;
        let startY = 0;
        let lastPointerPick = 0;
        this.container.addEventListener('pointerdown', (event) => {
          const option = event.target.closest('[data-rf-pick]');
          if (!option) return;
          event.preventDefault();
          armed = option;
          startX = event.clientX;
          startY = event.clientY;
        });
        this.container.addEventListener('pointermove', (event) => {
          if (
            armed &&
            (Math.abs(event.clientX - startX) > SLOP ||
              Math.abs(event.clientY - startY) > SLOP)
          ) {
            armed = null; // scrolling, not tapping
          }
        });
        this.container.addEventListener('pointercancel', () => {
          armed = null; // the browser took over (native scroll)
        });
        this.container.addEventListener('pointerup', () => {
          if (!armed) return;
          const option = armed;
          armed = null;
          lastPointerPick = Date.now();
          doPick(option);
        });
        this.container.addEventListener('click', (event) => {
          const option = event.target.closest('[data-rf-pick]');
          if (!option) return;
          event.preventDefault();
          if (Date.now() - lastPointerPick < 500) return;
          doPick(option);
        });
      }

      // Pointer outside the search field closes the suggestions; focus moves
      // within the field (input <-> options) keep them open.
      this.outsidePointerDown = (event) => {
        if (this.scope && !this.scope.contains(event.target)) this.close();
      };
      document.addEventListener('pointerdown', this.outsidePointerDown);
      if (this.scope) {
        this.scope.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !this.scope.contains(event.relatedTarget)) {
            this.close();
          }
        });
      }
    }

    render(raw) {
      if (!this.enabled) return;
      const result = this.getItems(raw);
      const container = this.container;
      container.textContent = '';
      this.entries = [];
      this.picks = [];
      this.active = -1;

      // Options share the entries/picks arrays regardless of group, so the
      // pointer delegation (data-rf-pick index) and keyboard nav cover the
      // no-results fallback items exactly like regular suggestions.
      const addOption = (item) => {
        const index = this.picks.length;
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'rf__ac-item';
        option.setAttribute('role', 'option');
        option.id = this.idPrefix + index;
        if (item.img) {
          const img = document.createElement('img');
          img.className = 'rf__ac-thumb';
          img.src = item.img;
          img.alt = '';
          img.loading = 'lazy';
          option.append(img);
        } else {
          const placeholder = document.createElement('span');
          placeholder.className = 'rf__ac-thumb';
          option.append(placeholder);
        }
        const meta = document.createElement('span');
        meta.className = 'rf__ac-meta';
        const title = document.createElement('span');
        title.className = 'rf__ac-title';
        title.textContent = item.title;
        meta.append(title);
        if (item.sub) {
          const sub = document.createElement('span');
          sub.className = 'rf__ac-sub';
          sub.textContent = item.sub;
          meta.append(sub);
        }
        option.append(meta);
        option.setAttribute('data-rf-pick', String(index));
        this.picks.push(item.pick);
        container.append(option);
        this.entries.push(option);
      };

      result.items.forEach(addOption);

      // No matches: the empty note, then (when the consumer provides them)
      // the fallback products — e.g. the customizable "any race" frames.
      const fallbackItems =
        result.items.length === 0 && result.fallback ? result.fallback : [];
      if (result.items.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'rf__ac-empty';
        empty.textContent = this.strings.empty;
        container.append(empty);
      }
      if (fallbackItems.length) {
        if (this.strings.fallbackLabel) {
          const label = document.createElement('div');
          label.className = 'rf__ac-fallback-label';
          label.textContent = this.strings.fallbackLabel;
          container.append(label);
        }
        fallbackItems.forEach(addOption);
      }

      // Bottom row applies the raw typed query — pointless next to the
      // fallback group when there is nothing to "show all" of.
      if (!(result.total === 0 && fallbackItems.length)) {
        const all = document.createElement('button');
        all.type = 'button';
        all.className = 'rf__ac-all';
        all.setAttribute('role', 'option');
        all.id = this.idPrefix + 'all';
        all.textContent =
          result.total === 1
            ? this.strings.allOne
            : fmt(this.strings.allMany, { count: this.formatNumber(result.total) });
        all.setAttribute('data-rf-pick', 'all');
        this.allPick = result.allPick;
        container.append(all);
        this.entries.push(all);
      } else {
        this.allPick = result.allPick;
      }

      container.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    move(direction) {
      if (this.entries.length === 0) return;
      this.active =
        (this.active + direction + this.entries.length) % this.entries.length;
      this.entries.forEach((el, i) => {
        el.classList.toggle('is-active', i === this.active);
      });
      const active = this.entries[this.active];
      active.scrollIntoView({ block: 'nearest' });
      this.input.setAttribute('aria-activedescendant', active.id);
    }

    close() {
      window.clearTimeout(this.timer);
      if (!this.container) return;
      this.container.hidden = true;
      this.container.textContent = '';
      this.entries = [];
      this.picks = [];
      this.active = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    // Dismisses reopen attempts for a moment after a pick: the mobile
    // keyboard dismissal synthesizes focus events that would reopen the list.
    suppress(ms) {
      this.suppressUntil = Date.now() + (ms || 500);
    }

    destroy() {
      window.clearTimeout(this.timer);
      document.removeEventListener('pointerdown', this.outsidePointerDown);
    }
  }

  /* Aligns the content's left edge with the header logo on desktop — same
     mechanism as the theme's search-field section. Returns null when the
     selector is empty; otherwise { sync, destroy }. No-op below 750px or
     when the selector matches nothing. */
  function createLogoAlign(opts) {
    const selector = (opts.selector || '').trim();
    if (!selector) return null;
    const root = opts.root;
    const innerSelector = opts.innerSelector || '.rf__inner';
    const findLogo = () => {
      try {
        return document.querySelector(selector);
      } catch (e) {
        return null;
      }
    };
    const sync = () => {
      const inners = root.querySelectorAll(innerSelector);
      inners.forEach((el) => {
        el.style.removeProperty('padding-left');
        el.style.removeProperty('padding-right');
      });
      if (window.innerWidth < 750) return;
      const logo = findLogo();
      if (!logo) return;
      const logoLeft = logo.getBoundingClientRect().left;
      inners.forEach((el) => {
        const pad = Math.round(logoLeft - el.getBoundingClientRect().left);
        if (pad > 0 && pad < window.innerWidth / 2) {
          el.style.paddingLeft = pad + 'px';
          el.style.paddingRight = pad + 'px';
        }
      });
    };
    let timer = null;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(sync, 120);
    };
    sync();
    window.addEventListener('load', sync);
    window.addEventListener('resize', onResize);
    document.addEventListener('shopify:section:load', sync);
    document.addEventListener('shopify:section:select', sync);
    return {
      sync,
      destroy() {
        window.clearTimeout(timer);
        window.removeEventListener('load', sync);
        window.removeEventListener('resize', onResize);
        document.removeEventListener('shopify:section:load', sync);
        document.removeEventListener('shopify:section:select', sync);
      },
    };
  }

  /* No-results fallback for the suggestion dropdowns: maps the sections'
     configured products (config.fallbackProducts: {t,u,i}) to autocomplete
     items whose pick navigates to the product page. */
  function fallbackAcItems(config) {
    const list = (config && config.fallbackProducts) || [];
    return list.map(function (p) {
      return { img: p.i || null, title: p.t, sub: null, pick: { url: p.u } };
    });
  }

  window.MMCRaceSearch = {
    delay,
    hashString,
    normalizeText,
    fallbackAcItems,
    raceBaseTitle,
    raceGroupKey,
    variantLabel,
    variantOrder,
    TYPE_LABELS,
    normalizeQuery,
    matchesQuery,
    fmt,
    sessionGet,
    sessionSet,
    parseCoord,
    sortCollections,
    IndexLoader,
    Autocomplete,
    createLogoAlign,
  };
})();
